import React, { useRef, useState } from 'react';

import { MENTION_LABEL } from '../../../../../../../../store/dashboard/askAica/constants';

/**
 * The single space that follows a completed mention. `@chuckNorris` is only
 * recognised when it ends at a non-word character (see `containsCompleteMention`),
 * so without this separator the next character the user types silently
 * un-completes the mention and the turn stops routing as feedback.
 */
const MENTION_SPACE = ' ';

/**
 * Returns true when the text contains a complete `@chuckNorris` mention that is
 * either at the start of the string or preceded by whitespace, and either at the
 * end of the string or followed by a non-word character.
 *
 * Does NOT match:
 *   - email addresses like `abheesht@recur.club` (@ preceded by a word char)
 *   - partial mentions like `@chuck` or `@chuckN`
 *   - `@ 20%` (@ followed immediately by whitespace)
 */
export const containsCompleteMention = (text: string): boolean =>
    /(?:^|(?<=\s))@chuckNorris(?=$|\W)/i.test(text);

/**
 * If the text immediately to the LEFT of the caret ends with a partial mention
 * that could still complete to `@chuckNorris`, returns that trailing partial
 * string (e.g. `@c`, `@chuck`). Returns null when there is no such partial at
 * the caret, including when the mention is already complete.
 *
 * Detection is anchored to the caret, NOT the end of the whole value: a partial
 * typed mid-string (e.g. caret at the start with existing text to its right)
 * must still open the popover.  `caretPos` defaults to the end of the string so
 * the pure detector stays usable without a caret.
 */
export const getPartialMentionSuffix = (text: string, caretPos: number = text.length): string | null => {
    const HANDLE = 'chuckNorris';
    // Only consider what the user has typed up to the caret — text after the
    // caret is not part of the token being edited.
    const left = text.slice(0, caretPos);
    // Match a trailing @ followed by 0–(length of handle - 1) chars that are
    // a case-insensitive prefix of the handle.  The @ must be preceded by
    // start-of-string or whitespace so emails are excluded.
    // `@[a-z]*` already matches a trailing bare `@` (zero letters), so no separate
    // bare-`@` fallback is needed.
    const m = left.match(/(?:^|(?<=\s))(@[a-z]*)$/i);
    if (!m) return null;
    const partial = m[1]; // e.g. "@chuck" or "@"
    // Must be a prefix of "@chuckNorris" (case-insensitive) and not the full mention
    const full = `@${HANDLE}`;
    if (!full.toLowerCase().startsWith(partial.toLowerCase())) return null;
    if (partial.toLowerCase() === full.toLowerCase()) return null; // already complete
    return partial;
};

/**
 * True when the text ENDS with a complete `@chuckNorris` — the caret-anchored
 * form of `containsCompleteMention` (same start boundary, no trailing lookahead
 * because the caret is the boundary). Used to detect the keystroke that just
 * finished a hand-typed mention.
 */
export const endsWithCompleteMention = (text: string): boolean => /(?:^|(?<=\s))@chuckNorris$/i.test(text);

/**
 * The typed path's auto space: when the character just typed completed a
 * `@chuckNorris` at the caret, returns the value with a single space inserted at
 * the caret (and the caret moved past it). Returns null when nothing should change.
 *
 * Three guards keep it from fighting the user:
 *   - grow-only — a deletion never re-inserts, so Backspace can remove the space
 *     for good instead of it springing back
 *   - already-spaced — never doubles up when a space already follows the caret
 *   - complete-at-caret — only the keystroke that finishes the mention fires;
 *     typing further along an existing mention does nothing
 */
export const applyMentionAutoSpace = (
    previous: string,
    value: string,
    caretPos: number,
): { value: string; caret: number } | null => {
    if (value.length <= previous.length) return null;
    if (!endsWithCompleteMention(value.slice(0, caretPos))) return null;
    if (value.slice(caretPos).startsWith(MENTION_SPACE)) return null;
    return {
        value: value.slice(0, caretPos) + MENTION_SPACE + value.slice(caretPos),
        caret: caretPos + MENTION_SPACE.length,
    };
};

/** Removes every `@chuckNorris` trigger and collapses the leftover whitespace. */
export const stripMention = (text: string): string =>
    text.replace(/@chuckNorris/gi, '').replace(/\s+/g, ' ').trim();

export interface UseFeedbackMention {
    /** True when the composer holds a complete `@chuckNorris` — routes the send as feedback. */
    feedbackMode: boolean;
    /** Whether the mention suggestion popover is currently shown. */
    showMentionDropdown: boolean;
    /**
     * Sync the popover visibility to the latest textarea value + caret position
     * (call from onChange). `caretPos` is the textarea's `selectionStart`.
     */
    syncDropdown: (value: string, caretPos?: number) => void;
    /**
     * Auto-space a hand-typed mention (call from onChange, before storing the value):
     * returns the value the caller should store and the caret that goes with it —
     * unchanged when no space was needed.
     */
    autoSpaceMention: (value: string, caretPos: number) => { value: string; caret: number };
    /**
     * Swallows the space keystroke that would duplicate a just-inserted auto space
     * (call from onKeyDown). Returns true when the key was consumed.
     */
    handleMentionSpaceKey: (e: React.KeyboardEvent) => boolean;
    /** Replace the trailing partial (`@chuck`) with the full `@chuckNorris ` and keep focus. */
    completeMentionInline: () => void;
    /** Close the popover without completing, keeping focus in the textarea. */
    dismissMentionDropdown: () => void;
    /**
     * Handle the dropdown's own keys (Enter/Tab to complete, Esc to dismiss).
     * Returns true when the key was consumed so the caller can stop.
     */
    handleMentionKey: (e: React.KeyboardEvent) => boolean;
    /** Backdrop innerHTML with each complete mention wrapped in the highlight span. */
    buildBackdropHtml: (text: string) => string;
    /** True when there is real feedback text beyond the mention itself (A-15 gate). */
    feedbackHasContent: boolean;
}

/**
 * Encapsulates the `@chuckNorris` mention affordance that turns an AskAica turn
 * into a feedback message: partial-mention detection, the suggestion popover,
 * inline completion, the highlight backdrop, and the feedback send/gate helpers.
 *
 * It operates on the composer's shared `message`/`setMessage`/`textareaRef` rather
 * than owning them, so the component keeps a single source of truth for the text
 * and the normal-send path stays where it is.
 *
 * @param highlightClassName the CSS-module class applied to a highlighted mention
 *        in the backdrop — injected so style ownership stays with the component.
 */
export const useFeedbackMention = (
    message: string,
    setMessage: React.Dispatch<React.SetStateAction<string>>,
    textareaRef: React.RefObject<HTMLTextAreaElement>,
    highlightClassName: string,
): UseFeedbackMention => {
    const [showMentionDropdown, setShowMentionDropdown] = useState(false);
    /**
     * Caret index sitting immediately after a space this hook inserted, or null.
     * Only alive until the next keystroke — it exists solely so the space the user
     * reflexively types next does not become a second space.
     */
    const autoSpacedCaretRef = useRef<number | null>(null);

    // Derived from message — no separate state needed.
    const feedbackMode = containsCompleteMention(message);

    const syncDropdown = (value: string, caretPos?: number) => {
        setShowMentionDropdown(getPartialMentionSuffix(value, caretPos) !== null);
    };

    const autoSpaceMention = (value: string, caretPos: number): { value: string; caret: number } => {
        const spaced = applyMentionAutoSpace(message, value, caretPos);
        if (!spaced) {
            autoSpacedCaretRef.current = null;
            return { value, caret: caretPos };
        }
        autoSpacedCaretRef.current = spaced.caret;
        // The value is stored by the caller, so only the caret needs restoring —
        // after the state update flushes, as with completeMentionInline.
        requestAnimationFrame(() => {
            textareaRef.current?.setSelectionRange(spaced.caret, spaced.caret);
        });
        return spaced;
    };

    const handleMentionSpaceKey = (e: React.KeyboardEvent): boolean => {
        if (e.key !== MENTION_SPACE) {
            // Any other key moves or edits away from the fresh auto space.
            autoSpacedCaretRef.current = null;
            return false;
        }
        const textarea = textareaRef.current;
        const caret = textarea?.selectionStart ?? null;
        const isCollapsedOnAutoSpace =
            autoSpacedCaretRef.current !== null &&
            caret !== null &&
            caret === textarea?.selectionEnd &&
            caret === autoSpacedCaretRef.current;
        if (!isCollapsedOnAutoSpace) return false;
        e.preventDefault();
        autoSpacedCaretRef.current = null;
        return true;
    };

    /**
     * Completes the partial mention at the caret inline (replaces e.g. `@chuck`
     * with `@chuckNorris `), preserving any text after the caret, and keeps focus
     * in the textarea with the caret positioned after the mention's trailing space
     * so whatever the user types next cannot un-complete the mention. A space that
     * is already there is reused rather than doubled.
     */
    const completeMentionInline = () => {
        const caret = textareaRef.current?.selectionStart ?? message.length;
        const partial = getPartialMentionSuffix(message, caret);
        if (!partial) {
            setShowMentionDropdown(false);
            return;
        }
        const before = message.slice(0, caret);
        const after = message.slice(caret);
        // The partial sits at the end of `before` (it matched there), so its
        // start index is where the replacement begins.
        const beforeMention = before.slice(0, before.lastIndexOf(partial));
        const alreadySpaced = after.startsWith(MENTION_SPACE);
        const newBefore = beforeMention + MENTION_LABEL + (alreadySpaced ? '' : MENTION_SPACE);
        // Either way the caret lands one space past the mention.
        const newCaret = newBefore.length + (alreadySpaced ? MENTION_SPACE.length : 0);
        setMessage(newBefore + after);
        setShowMentionDropdown(false);
        // Only guard against a duplicate when the space is one we just inserted.
        autoSpacedCaretRef.current = alreadySpaced ? null : newCaret;
        // Restore focus + caret after the state update flushes
        requestAnimationFrame(() => {
            if (textareaRef.current) {
                textareaRef.current.focus();
                textareaRef.current.setSelectionRange(newCaret, newCaret);
            }
        });
    };

    const dismissMentionDropdown = () => {
        setShowMentionDropdown(false);
        textareaRef.current?.focus();
    };

    const handleMentionKey = (e: React.KeyboardEvent): boolean => {
        if (!showMentionDropdown) return false;
        if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            completeMentionInline();
            return true;
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            dismissMentionDropdown();
            return true;
        }
        return false;
    };

    /**
     * Builds the backdrop innerHTML: HTML-escapes the raw text, then wraps every
     * complete `@chuckNorris` occurrence in a highlight span.  A trailing space
     * ensures the backdrop div matches the textarea's last-line height.
     */
    const buildBackdropHtml = (text: string): string => {
        const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const highlighted = escaped.replace(
            // Same boundary as containsCompleteMention so the highlight only fires
            // when a real mention is recognised (e.g. NOT inside `foo@chuckNorris`).
            /(?:^|(?<=\s))(@chuckNorris)(?=$|\W)/gi,
            `<span class="${highlightClassName}">$1</span>`,
        );
        return highlighted + ' ';
    };

    // In feedback mode: require non-empty text beyond the mention itself (A-15).
    const feedbackHasContent = message.replace(/@chuckNorris/gi, '').trim().length > 0;

    return {
        feedbackMode,
        showMentionDropdown,
        syncDropdown,
        autoSpaceMention,
        handleMentionSpaceKey,
        completeMentionInline,
        dismissMentionDropdown,
        handleMentionKey,
        buildBackdropHtml,
        feedbackHasContent,
    };
};
